import { Map as MlMap } from "maplibre-gl";
import { procedureGeometry, procedureLabels } from "./webgl-maplibre.procedure-geometry";
import { ProcedureLayer } from "./webgl-maplibre.procedure-layer";
import { expandedView, featureInView } from "./webgl-maplibre.viewport-cache";

export interface ProcedureAirport {
  airportId: string;
  airportElevationM: number;
}
export function procedureAirport(properties: GeoJSON.GeoJsonProperties): ProcedureAirport | undefined {
  const p = properties ?? {};
  return typeof p.airportId === "string" &&
    p.airportId.length > 0 &&
    typeof p.airportElevationM === "number" &&
    Number.isFinite(p.airportElevationM)
    ? { airportId: p.airportId, airportElevationM: p.airportElevationM }
    : undefined;
}

/** Procedures on flat sheets at their airport elevations, in one custom layer of the main map. */
export class ProcedurePlanes {
  private layer = new ProcedureLayer();
  private data?: GeoJSON.FeatureCollection;
  private shown: GeoJSON.Feature[] = [];
  private heliportZoom = false;
  constructor(private readonly map: MlMap) {
    map.addLayer(this.layer);
  }

  setData(data: GeoJSON.FeatureCollection): GeoJSON.FeatureCollection {
    this.data = data;
    const bounds = this.map.getBounds();
    const view = expandedView(
      { west: bounds.getWest(), south: bounds.getSouth(), east: bounds.getEast(), north: bounds.getNorth() },
      0.15
    );
    const raised: GeoJSON.Feature[] = [];
    const ground: GeoJSON.Feature[] = [];
    const zoom = this.map.getZoom();
    const minZoom = this.map.getLayer("vm-runway-labels")?.minzoom ?? 0;
    for (const f of data.features) {
      const airport = zoom >= minZoom && f.properties?._objType === 15 ? procedureAirport(f.properties) : undefined;
      // The layer draws lines and polygons only; points stay with the 2D circle layer.
      const point = f.geometry?.type === "Point" || f.geometry?.type === "MultiPoint";
      if (!airport || point) ground.push(f);
      else if (featureInView(f, view)) raised.push(f);
    }
    // Rebuild the buffers only when the procedures in view change, or when the
    // heliport sector fill rule (zoom 15) flips.
    const heliportZoom = zoom >= 15;
    if (
      heliportZoom !== this.heliportZoom ||
      raised.length !== this.shown.length ||
      raised.some((f, i) => f !== this.shown[i])
    ) {
      this.shown = raised;
      this.heliportZoom = heliportZoom;
      this.layer.setData(procedureGeometry(raised, zoom), procedureLabels(raised));
    }
    return { ...data, features: ground };
  }

  remove(): void {
    this.data = undefined;
    if (this.map.getLayer(this.layer.id)) this.map.removeLayer(this.layer.id);
  }

  refreshViewport(): void {
    if (this.data) this.setData(this.data);
  }
}
